import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthProvider';
import { AdminRoute } from './components/AdminRoute';
import { FeatureRoute, SuperAdminRoute } from './components/FeatureRoute';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/LoginPage';
import { Dashboard } from './pages/Dashboard';
import { MembersPage } from './pages/MembersPage';
import { EventsPage } from './pages/EventsPage';
import { QuizPage } from './pages/QuizPage';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { AdminPage } from './pages/AdminPage';
import { SuperAdminPage } from './pages/SuperAdminPage';
import { ProfilePage } from './pages/ProfilePage';
import { CompleteProfilePage } from './pages/CompleteProfilePage';
import { SponsorsPage } from './pages/SponsorsPage';
import { QuizPreviewPage } from './pages/QuizPreviewPage';
import { ScoringAdminPage } from './pages/ScoringAdminPage';
import { CheckInPage } from './pages/CheckInPage';
import { ScoringShooterPage } from './pages/ScoringShooterPage';
import { Round1TeamsPage } from './pages/Round1TeamsPage';
import { Round2Page } from './pages/Round2Page';
import { Round2ShooterPage } from './pages/Round2ShooterPage';
import { ScoreboardPage } from './pages/ScoreboardPage';
import { BigScreenPage } from './pages/BigScreenPage';
import { needsProfileCompletion } from './lib/profile-completion';
import { isPublicScoreboardPath } from './lib/shooting';
import { pageTitle } from './lib/page-title';
import { SCORING_CHECK_IN_CALLER_ROUTE, SCORING_CHECK_IN_ROUTE, SCORING_LIST_ROUTE, SCORING_ROUND1_TEAMS_ROUTE, SCORING_ROUND2_ROUTE, SCORING_ROUND2_SHOOTER_ROUTE, SCORING_SHOOTER_ROUTE, SCORING_TOURNAMENT_ROUTE } from './lib/scoring-routes';

export function AppRoutes() {
  const location = useLocation();
  const { member, loading } = useAuth();

  // Tab titles per page (scoring and scoreboard pages set their own).
  useEffect(() => {
    if (loading) return;
    const title = pageTitle(location.pathname, location.search, Boolean(member));
    if (title) document.title = title;
  }, [location.pathname, location.search, member, loading]);

  if (isPublicScoreboardPath(location.pathname)) {
    return (
      <Routes>
        <Route path="/scoreboard" element={<ScoreboardPage />} />
        <Route path="/scoreboard/:slug/display" element={<BigScreenPage />} />
        <Route path="/scoreboard/:slug" element={<ScoreboardPage />} />
      </Routes>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-surface-0)' }}>
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-transparent" style={{ borderTopColor: 'var(--color-accent)', borderBottomColor: 'var(--color-accent)' }}></div>
      </div>
    );
  }

  if (!member) {
    return <LoginPage />;
  }

  if (needsProfileCompletion(member)) {
    return (
      <Routes>
        <Route path="/complete-profile" element={<CompleteProfilePage />} />
        <Route path="*" element={<Navigate to="/complete-profile" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Layout><Dashboard /></Layout>} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route
        path="/quiz"
        element={
          <FeatureRoute feature="quiz">
            <QuizPage />
          </FeatureRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <FeatureRoute feature="leaderboard">
            <LeaderboardPage />
          </FeatureRoute>
        }
      />
      <Route
        path="/sponsors"
        element={
          <FeatureRoute feature="quiz">
            <SponsorsPage />
          </FeatureRoute>
        }
      />
      <Route
        path="/events"
        element={
          <FeatureRoute feature="events">
            <EventsPage />
          </FeatureRoute>
        }
      />
      <Route
        path="/members"
        element={
          <AdminRoute>
            <FeatureRoute feature="members">
              <MembersPage />
            </FeatureRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute allowOrganizer>
            <FeatureRoute feature="admin">
              <AdminPage />
            </FeatureRoute>
          </AdminRoute>
        }
      />
      <Route
        path={SCORING_ROUND2_SHOOTER_ROUTE}
        element={
          <AdminRoute allowOrganizer>
            <FeatureRoute feature="admin">
              <Round2ShooterPage />
            </FeatureRoute>
          </AdminRoute>
        }
      />
      <Route
        path={SCORING_ROUND1_TEAMS_ROUTE}
        element={
          <AdminRoute allowOrganizer>
            <FeatureRoute feature="admin">
              <Round1TeamsPage />
            </FeatureRoute>
          </AdminRoute>
        }
      />
      <Route
        path={SCORING_ROUND2_ROUTE}
        element={
          <AdminRoute allowOrganizer>
            <FeatureRoute feature="admin">
              <Round2Page />
            </FeatureRoute>
          </AdminRoute>
        }
      />
      <Route
        path={SCORING_CHECK_IN_CALLER_ROUTE}
        element={
          <AdminRoute allowOrganizer>
            <FeatureRoute feature="admin">
              <CheckInPage mode="caller" />
            </FeatureRoute>
          </AdminRoute>
        }
      />
      <Route
        path={SCORING_CHECK_IN_ROUTE}
        element={
          <AdminRoute allowOrganizer>
            <FeatureRoute feature="admin">
              <CheckInPage mode="desk" />
            </FeatureRoute>
          </AdminRoute>
        }
      />
      {[SCORING_LIST_ROUTE, SCORING_TOURNAMENT_ROUTE].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <AdminRoute allowOrganizer>
              <FeatureRoute feature="admin">
                <ScoringAdminPage />
              </FeatureRoute>
            </AdminRoute>
          }
        />
      ))}
      <Route
        path={SCORING_SHOOTER_ROUTE}
        element={
          <AdminRoute allowOrganizer>
            <FeatureRoute feature="admin">
              <ScoringShooterPage />
            </FeatureRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/quiz-preview/:campaignId/:sessionId?"
        element={
          <AdminRoute>
            <FeatureRoute feature="admin">
              <QuizPreviewPage />
            </FeatureRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/super-admin"
        element={
          <SuperAdminRoute>
            <SuperAdminPage />
          </SuperAdminRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
